import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, useApp } from '../bizsuite-app';

function Skeleton({ width, height, style }) {
  return (
    <div style={{
      width: width || '100%', height: height || 20,
      background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 6, ...style,
    }} />
  );
}

function HBarChart({ data, labelKey, valueKey, formatValue, colorFn }) {
  if (!data || data.length === 0) return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No data</div>;
  const max = Math.max(...data.map(d => parseFloat(d[valueKey]) || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((item, idx) => {
        const val = parseFloat(item[valueKey]) || 0;
        const w = (val / max) * 100;
        const color = colorFn ? colorFn(item, idx) : '#6366f1';
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 120, fontSize: 12, color: '#64748B', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item[labelKey]}>
              {item[labelKey] || 'Unknown'}
            </div>
            <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 22, overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(w, 2)}%`, background: color, height: '100%', borderRadius: 4, transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ width: 70, fontSize: 12, fontWeight: 600, color: '#334155', textAlign: 'right', flexShrink: 0 }}>
              {formatValue ? formatValue(val) : val}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, sub, color, loading }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 12, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      {loading ? <Skeleton width={80} height={32} /> : <div style={{ fontSize: 28, fontWeight: 700, color: color || '#1e293b' }}>{value}</div>}
      {sub && <div style={{ fontSize: 12, color: '#94A3B8' }}>{sub}</div>}
    </div>
  );
}

function benchBucketColor(bucket) {
  if (bucket === '0-15') return '#10b981';
  if (bucket === '16-30') return '#f59e0b';
  if (bucket === '31-45') return '#f97316';
  if (bucket === '46-60') return '#ef4444';
  return '#dc2626';
}

export default function HRDashboard() {
  const app = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch('/api/dashboards/hr');
      if (res.ok) setData(await res.json());
    } catch (e) { /* best effort */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const d = data || {};
  const totalHeadcount = (d.headcount_by_department || []).reduce((s, r) => s + (parseInt(r.count, 10) || 0), 0);
  const totalOpenPositions = (d.open_positions_by_department || []).reduce((s, r) => s + (parseInt(r.count, 10) || 0), 0);

  return (
    <div>
      <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>

      <p style={{ color: '#64748B', marginBottom: 20, marginTop: -16 }}>
        Human capital overview and workforce analytics.
      </p>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <StatCard label="Total Headcount" value={totalHeadcount} color="#6366f1" loading={loading} />
        <StatCard label="Attrition Rate (12M)" value={`${d.attrition_rate || 0}%`} color={d.attrition_rate > 15 ? '#ef4444' : '#10b981'} loading={loading} />
        <StatCard label="Open Positions" value={totalOpenPositions} color="#f59e0b" loading={loading} />
        <StatCard label="Skills Gaps" value={d.skills_gap?.length || 0} sub="Unmet demand" color="#ef4444" loading={loading} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Headcount by Department */}
        <div className="card">
          <div className="card-header"><h2>Headcount by Department</h2></div>
          <div className="card-body">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3,4].map(i => <Skeleton key={i} height={22} />)}</div>
            ) : (
              <HBarChart
                data={d.headcount_by_department || []}
                labelKey="department"
                valueKey="count"
                colorFn={(_, idx) => ['#6366f1', '#8b5cf6', '#a855f7', '#c084fc', '#10b981', '#f59e0b'][idx % 6]}
              />
            )}
          </div>
        </div>

        {/* Leave Utilization */}
        <div className="card">
          <div className="card-header"><h2>Leave Utilization</h2></div>
          <div className="card-body">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
            ) : (d.leave_summary || []).length === 0 ? (
              <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No leave data</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(d.leave_summary || []).map((leave, idx) => (
                  <div key={idx}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, color: '#334155' }}>{leave.leave_type || 'Unknown'}</span>
                      <span style={{ color: '#64748B' }}>{leave.utilization_pct}% used ({leave.total_taken}/{leave.total_entitled})</span>
                    </div>
                    <div style={{ background: '#f1f5f9', borderRadius: 4, height: 12, overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(leave.utilization_pct, 100)}%`,
                        background: leave.utilization_pct > 80 ? '#ef4444' : leave.utilization_pct > 50 ? '#f59e0b' : '#10b981',
                        height: '100%', borderRadius: 4, transition: 'width 0.6s ease',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Open Positions Table */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><h2>Open Positions</h2></div>
        <div className="card-body">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
          ) : (d.open_positions_by_department || []).length === 0 ? (
            <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No open positions</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748B' }}>Department</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748B' }}>Job Title</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748B' }}>Location</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: '#64748B' }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.open_positions_by_department || []).map((pos, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{pos.department}</td>
                      <td style={{ padding: '8px 12px' }}>{pos.job_title || '-'}</td>
                      <td style={{ padding: '8px 12px', color: '#64748B' }}>{pos.location || '-'}</td>
                      <td style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600 }}>{pos.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Skills Gap */}
        <div className="card">
          <div className="card-header"><h2>Skills Gap</h2></div>
          <div className="card-body">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
            ) : (d.skills_gap || []).length === 0 ? (
              <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No skills gaps detected</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(d.skills_gap || []).map((sg, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <div style={{ flex: 1, fontWeight: 500 }}>{sg.skill}</div>
                    <div style={{ color: '#10b981', width: 60, textAlign: 'center' }} title="Supply">{sg.supply} avail</div>
                    <div style={{ color: '#ef4444', width: 60, textAlign: 'center' }} title="Demand">{sg.demand} needed</div>
                    <div style={{
                      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      background: sg.gap > 3 ? '#fef2f2' : '#fefce8',
                      color: sg.gap > 3 ? '#dc2626' : '#ca8a04',
                    }}>
                      Gap: {sg.gap}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bench Aging */}
        <div className="card">
          <div className="card-header"><h2>Bench Aging</h2></div>
          <div className="card-body">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
            ) : (d.bench_aging || []).length === 0 ? (
              <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No bench data</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(d.bench_aging || []).map((ba, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <div style={{ width: 80, fontWeight: 500, color: '#334155' }}>{ba.bucket} days</div>
                    <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 22, overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.max((parseInt(ba.count, 10) / Math.max(...(d.bench_aging || []).map(b => parseInt(b.count, 10) || 0), 1)) * 100, 5)}%`,
                        background: benchBucketColor(ba.bucket),
                        height: '100%', borderRadius: 4, transition: 'width 0.6s ease',
                      }} />
                    </div>
                    <div style={{ width: 40, textAlign: 'right', fontWeight: 600, color: benchBucketColor(ba.bucket) }}>
                      {ba.count}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Certification Expiry */}
      <div className="card">
        <div className="card-header"><h2>Certification Expiry (Next 90 Days)</h2></div>
        <div className="card-body">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
          ) : (d.certification_expiry || []).length === 0 ? (
            <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No certifications expiring soon</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748B' }}>Employee</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748B' }}>Certification</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748B' }}>Expiry Date</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: '#64748B' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.certification_expiry || []).map((cert, idx) => {
                    const daysLeft = cert.expiry_date ? Math.ceil((new Date(cert.expiry_date) - new Date()) / 86400000) : 0;
                    const urgency = daysLeft <= 30 ? 'critical' : daysLeft <= 60 ? 'warning' : 'ok';
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{cert.first_name} {cert.last_name}</td>
                        <td style={{ padding: '8px 12px' }}>{cert.certification_name}</td>
                        <td style={{ padding: '8px 12px', color: '#64748B' }}>{cert.expiry_date}</td>
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                            background: urgency === 'critical' ? '#fef2f2' : urgency === 'warning' ? '#fefce8' : '#f0fdf4',
                            color: urgency === 'critical' ? '#dc2626' : urgency === 'warning' ? '#ca8a04' : '#16a34a',
                          }}>
                            {daysLeft}d left
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
